import PersonAddIcon from '@mui/icons-material/PersonAdd'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Button, Dialog, Box, Typography, IconButton, Tooltip, useTheme, Menu, MenuItem, Divider, CircularProgress, Chip } from '@mui/material'
import { useState, useEffect, useContext, useMemo, useCallback, useRef, memo } from 'react'
import { useNavigate } from 'react-router'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LoginIcon from '@mui/icons-material/Login'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import CheckInDialogComponent from 'src/clinic/components/CheckIn'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { toast } from 'react-toastify'
import { getMPDataWithParams } from 'src/mentor/services'
import { getCPDataWithParams } from 'src/clinic/services'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'

dayjs.extend(utc)
import { components } from 'react-select'
import { styled } from '@mui/material/styles'
import {
  CheckInInformationComponent,
  CheckoutButtonComponent,
} from 'src/clinic/components/CheckIn/checkInComponents'
import { PastTherapyReportDialogPaper } from 'src/clinic/components/ClinicManageBookingsPage/receiptPrinting/styledComponents'
import BaseUITabsComponent from 'src/common/tabs/BaseUITabs'
import { useScreenSize } from 'src/customHooks/customHooks'
import { MENTOR_NEW_BOOKING_TABS } from 'src/mentor/components/MentorClinicBookingsPage/component/newBooking/newBookingConsts'
import NewBookingPopup from 'src/mentor/components/MentorClinicBookingsPage/component/newBooking/NewBookingPopup'
import { MENTOR_CLINIC_BOOKING_TABS_LIST } from 'src/mentor/components/MentorClinicBookingsPage/util'
import CheckinRequestPromptDialog from 'src/mentor/components/CheckinRequestPromptDialog'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import {
  createEmrCheckInRequest,
  getCheckInRequestErrorMessage,
} from 'src/mentor/services/checkInRequestService'
import SimplePractitionerButton from 'src/clinic/components/ClinicManageBookingsPage/components/SimplePractitionerButton'
import ClinicAssistantNameButton from 'src/clinic/components/ClinicAssistantProfileDrawer/ClinicAssistantNameButton'

export const ClinicSelectValueComp = (props) => {
  const item = localStorage.getItem('checkedInDetails')

  if (!item) {
    return <div></div>
  }

  const parsedItem = JSON.parse(item)
  return (
    <components.SingleValue {...props}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ display: 'flex', alignItems: 'center' }}>{props.children}</span>
        <span
          style={{ display: 'flex', alignItems: 'center', whiteSpace: 'break-spaces' }}
          className="fs-7"
        >
          {parsedItem?.roomName}
        </span>
      </div>
    </components.SingleValue>
  )
}

function HeaderItems({
  clinicAssociationData,
  clinicId,
  clinicBrandId,
  selectedTab,
  setSelectedTab,
  mentorProfileData,
  refreshData,
  hideRefreshButton = false,
  hideClinicDetails = false,
  hideCheckinDetails = false,
  hideTabs = false,
  mentorsList = [],
  searchPatientApiSignature = null,
  appointmentTypesList = [],
  createPatientApiSignature = null,
  excludeTabs = [],
  patientHistoryGetApiSignature,
  hidePatientHistoryRelatedTabs = false,
  hidePatientAddButton = false,
  hideCheckInButton = false,
  customExitButton = null,
  hideSendToDoctorButton = false,
  onOpenPatientSearch,
  children, // Keep for backward compatibility, but prefer practitionerButtonProps
  practitionerButtonProps, // New: primitive props for practitioner button
  onOpenPractitionerDrawer, // New: handler for opening practitioner drawer
  onAssistantNameClick, // Handler for clicking clinic assistant name (optional)
  isClinicAssistantFlow = false, // Flag to indicate clinic assistant flow
}) {
  const navigate = useNavigate()
  const mentorProfileContext = useContext(MentorProfileContext)
  const contextMentorProfileData = mentorProfileContext?.mentorProfileData
  const mentorProfile = mentorProfileData || contextMentorProfileData
  const [selectedPatient, setSelectedPatient] = useState()
  const [refreshing, setRefreshing] = useState(false)
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null)
  const [checkedInTime, setCheckedInTime] = useState(null)
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  const [isPeekingIn, setIsPeekingIn] = useState(false)
  const [peekedRoomData, setPeekedRoomData] = useState(null)
  const [checkInModalOpen, setCheckInModalOpen] = useState(false)
  const [dialogRoomData, setDialogRoomData] = useState(null) // Snapshot for dialog to prevent blinking
  const [requestCheckoutDialogOpen, setRequestCheckoutDialogOpen] = useState(false)
  const [requestCheckoutRoomData, setRequestCheckoutRoomData] = useState(null)
  const [requestCheckoutLoading, setRequestCheckoutLoading] = useState(false)
  const checkInModalOpenRef = useRef(false) // Ref to track dialog state without causing re-renders
  const menuOpenTimeRef = useRef(null) // Track when menu was opened to prevent immediate closing
  const screenSize = useScreenSize()
  const theme = useTheme()

  const currentClinic = clinicAssociationData?.find((cln) => cln?.id === clinicId)

  // Disable body interactions when menu is open
  useEffect(() => {
    const isMenuOpen = Boolean(mobileMenuAnchor)
    
    if (isMenuOpen) {
      // Store original styles
      const originalBodyPointerEvents = document.body.style.pointerEvents
      const originalBodyOverflow = document.body.style.overflow
      const originalHtmlOverflow = document.documentElement.style.overflow
      
      // Disable pointer events on body to prevent clicks from closing menu
      document.body.style.pointerEvents = 'none'
      // Allow scrolling in menu but prevent body scroll
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
      
      // Re-enable pointer events only on the menu and its container
      const menuElement = document.querySelector('[class*="MuiMenu-root"][aria-hidden="false"]')
      if (menuElement) {
        menuElement.style.pointerEvents = 'auto'
      }
      
      // Re-enable pointer events on the header container
      const headerContainer = document.querySelector('[data-portal-header="true"]')
      if (headerContainer) {
        headerContainer.style.pointerEvents = 'auto'
      }
      
      return () => {
        // Restore original styles
        document.body.style.pointerEvents = originalBodyPointerEvents
        document.body.style.overflow = originalBodyOverflow
        document.documentElement.style.overflow = originalHtmlOverflow
      }
    }
  }, [mobileMenuAnchor])

  // Keep ref in sync with state
  useEffect(() => {
    checkInModalOpenRef.current = checkInModalOpen
  }, [checkInModalOpen])

  // Close dialog and clear snapshot - memoized to prevent re-renders
  const handleCloseCheckInDialog = useCallback(() => {
    setCheckInModalOpen(false)
    // Clear dialog room data after a short delay to prevent flicker
    setTimeout(() => {
      setDialogRoomData(null)
    }, 300)
  }, [])

  const openRequestCheckoutDialog = useCallback((roomData) => {
    setRequestCheckoutRoomData(roomData)
    setRequestCheckoutDialogOpen(true)
  }, [])

  // Auto-close check-in dialog after 30 seconds of inactivity
  useEffect(() => {
    if (!checkInModalOpen) {
      return
    }

    const autoCloseTimer = setTimeout(() => {
      handleCloseCheckInDialog()
      // Silently close without toast notification
    }, 30000) // 30 seconds

    return () => {
      clearTimeout(autoCloseTimer)
    }
  }, [checkInModalOpen, handleCloseCheckInDialog])

  // Get check-in status and peek status from localStorage
  useEffect(() => {
    const getCheckInStatus = () => {
      const checkedInDetails = localStorage.getItem('checkedInDetails')
      const peekedInDetails = localStorage.getItem('peekedInRoomDetails')
      
      let checkInTime = null
      let checkedIn = false
      let peeking = false
      let roomData = null
      
      if (checkedInDetails) {
        try {
          const parsed = JSON.parse(checkedInDetails)
          if (parsed?.createdTime) {
            checkInTime = dayjs.utc(parsed.createdTime).local()
            checkedIn = true
          }
        } catch (e) {
          console.error('Error parsing checkedInDetails:', e)
        }
      }
      
      if (peekedInDetails && !checkedIn) {
        try {
          const parsed = JSON.parse(peekedInDetails)
          if (parsed?.id) {
            peeking = true
            roomData = parsed
          }
        } catch (e) {
          console.error('Error parsing peekedInRoomDetails:', e)
        }
      }
      
      return { checkInTime, checkedIn, peeking, roomData }
    }

    const status = getCheckInStatus()
    setCheckedInTime(status.checkInTime)
    setIsCheckedIn(status.checkedIn)
    setIsPeekingIn(status.peeking)
    // Only update peekedRoomData if dialog is not open to prevent blinking
    if (!checkInModalOpenRef.current) {
      setPeekedRoomData(status.roomData)
    }
    
    // Update every second to keep status current
    const interval = setInterval(() => {
      const newStatus = getCheckInStatus()
      setCheckedInTime(newStatus.checkInTime)
      setIsCheckedIn(newStatus.checkedIn)
      setIsPeekingIn(newStatus.peeking)
      // Only update peekedRoomData if dialog is not open to prevent blinking
      if (!checkInModalOpenRef.current) {
        setPeekedRoomData(newStatus.roomData)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  // Handle check-in prompt (from "Check In" button or "Peeking Privately" chip)
  const handleCheckInPrompt = async () => {
    if (!peekedRoomData) {
      toast.error('No room information available. Please select a room first.')
      return
    }

    if (!currentClinic?.id) {
      toast.error('No clinic information available.')
      return
    }

    const isClinicAssistant = getAllRolesInBoolean().isClinicAssistant

    try {
      let response
      let rooms = []
      let currentRoom = null

      if (isClinicAssistant) {
        // Use Clinic Assistant API
        response = await getCPDataWithParams(
          `/api/secure/clinic-assistant/associated/chamber-room/list`,
          {
            clinicId: currentClinic.id,
          },
        )

        if (response?.status === 200) {
          rooms = Array.isArray(response?.data) ? response?.data : []
          currentRoom = rooms.find((room) => room?.id === peekedRoomData?.id)
        }
      } else {
        // Use Mentor/Teacher API
        response = await getMPDataWithParams(`/api/secure/teacher/associated-chamber-rooms`, {
          clinicLocationId: currentClinic.id,
        })

        if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
          rooms = response?.data?.data?.['defined-room-access'] || []
          currentRoom = rooms.find((room) => room?.id === peekedRoomData?.id)
        }
      }

      if (response?.status === 200) {
        if (!currentRoom) {
          toast.error('Room not found. Please try again.')
          return
        }

        // For clinic assistants, room status checking might not be available in the same way
        // For mentors, check if room is occupied by someone else using fresh data
        if (!isClinicAssistant) {
          const roomOccupied = currentRoom?.occupied
          const occupiedByThisMentor = 
            roomOccupied && currentRoom?.checkedInMentorId === mentorProfile?.id
          const checkedInMentorName = currentRoom?.checkedInMentorName

          if (roomOccupied && !occupiedByThisMentor) {
            const updatedRoomData = {
              ...peekedRoomData,
              occupied: currentRoom.occupied,
              checkedInMentorId: currentRoom.checkedInMentorId,
              checkedInMentorName: currentRoom.checkedInMentorName,
            }
            localStorage.setItem('peekedInRoomDetails', JSON.stringify(updatedRoomData))
            setPeekedRoomData(updatedRoomData)
            openRequestCheckoutDialog(updatedRoomData)
            return
          }

          // Update localStorage with fresh room data before proceeding
          const updatedRoomData = {
            ...peekedRoomData,
            occupied: currentRoom.occupied,
            checkedInMentorId: currentRoom.checkedInMentorId,
            checkedInMentorName: currentRoom.checkedInMentorName,
          }
          localStorage.setItem('peekedInRoomDetails', JSON.stringify(updatedRoomData))
          setPeekedRoomData(updatedRoomData)
          setDialogRoomData(JSON.parse(JSON.stringify(updatedRoomData))) // Deep copy
        } else {
          // For clinic assistants, use the room data as-is
          const updatedRoomData = {
            ...peekedRoomData,
            ...currentRoom,
          }
          localStorage.setItem('peekedInRoomDetails', JSON.stringify(updatedRoomData))
          setPeekedRoomData(updatedRoomData)
          setDialogRoomData(JSON.parse(JSON.stringify(updatedRoomData))) // Deep copy
        }

        // Store a snapshot of updated room data for the dialog to prevent re-rendering issues
        setCheckInModalOpen(true)
      } else {
        // If API call fails, fall back to using cached data but show a warning
        toast.warning('Could not refresh room status. Using cached information.', { autoClose: 3000 })
        
        // For clinic assistants, skip room occupancy check
        // For mentors, check if room is occupied by someone else using cached data
        if (!isClinicAssistant) {
          const roomOccupied = peekedRoomData?.occupied
          const occupiedByThisMentor = 
            roomOccupied && peekedRoomData?.checkedInMentorId === mentorProfile?.id
          const checkedInMentorName = peekedRoomData?.checkedInMentorName

          if (roomOccupied && !occupiedByThisMentor) {
            openRequestCheckoutDialog(peekedRoomData)
            return
          }
        }

        // Store a snapshot of room data for the dialog to prevent re-rendering issues
        setDialogRoomData(JSON.parse(JSON.stringify(peekedRoomData))) // Deep copy
        setCheckInModalOpen(true)
      }
    } catch (error) {
      console.error('Error fetching room status:', error)
      toast.error('Failed to refresh room status. Please try again.', { autoClose: 3000 })
      
      // For clinic assistants, skip room occupancy check
      // For mentors, fall back to using cached data
      if (!isClinicAssistant) {
        const roomOccupied = peekedRoomData?.occupied
        const occupiedByThisMentor = 
          roomOccupied && peekedRoomData?.checkedInMentorId === mentorProfile?.id
        const checkedInMentorName = peekedRoomData?.checkedInMentorName

        if (roomOccupied && !occupiedByThisMentor) {
          openRequestCheckoutDialog(peekedRoomData)
          return
        }
      }

      // Store a snapshot of room data for the dialog to prevent re-rendering issues
      setDialogRoomData(JSON.parse(JSON.stringify(peekedRoomData))) // Deep copy
      setCheckInModalOpen(true)
    }
  }

  // Memoize dialog props to prevent unnecessary re-renders
  const checkInDialogProps = useMemo(() => {
    if (!checkInModalOpen || !currentClinic || !dialogRoomData) {
      return null
    }
    return {
      closeFunction: handleCloseCheckInDialog,
      roomData: dialogRoomData,
      clinicLocationData: currentClinic,
      onChoosingInPerson: async () => {
        handleCloseCheckInDialog()
        // Refresh will happen automatically via the useEffect
      },
      onChoosingRemote: () => {
        handleCloseCheckInDialog()
        navigate(
          `/mentor-home/join-chamber-room/${currentClinic?.clinicBrandId}/${currentClinic?.id}`,
        )
      },
    }
  }, [checkInModalOpen, currentClinic, dialogRoomData, handleCloseCheckInDialog, navigate])

  const handleSubmitCheckoutRequest = async (reason) => {
    if (!requestCheckoutRoomData?.id) {
      toast.error('No occupied room found for this request.')
      return
    }

    setRequestCheckoutLoading(true)

    try {
      const { request, response } = await createEmrCheckInRequest({
        chamberRoomId: requestCheckoutRoomData.id,
        reason,
      })

      if (request) {
        setRequestCheckoutDialogOpen(false)
        setRequestCheckoutRoomData(null)
        toast.success(
          `Checkout request sent to ${
            requestCheckoutRoomData?.checkedInMentorName || 'the checked-in mentor'
          }.`,
        )
        return
      }

      toast.error(
        getCheckInRequestErrorMessage(response, 'Could not send the checkout request.'),
      )
    } catch (error) {
      console.error('Error sending checkout request:', error)
      toast.error('Could not send the checkout request.')
    } finally {
      setRequestCheckoutLoading(false)
    }
  }

  const handleClick = (e) => {
    setSelectedTab(e)
  }

  const handleMobileMenuOpen = (event) => {
    event.stopPropagation()
    event.preventDefault()
    menuOpenTimeRef.current = Date.now()
    const target = event.currentTarget
    // Only set anchor if the element is in the document and visible
    if (target && typeof document !== 'undefined' && document.body.contains(target)) {
      // Check if element is visible (not display: none)
      const style = window.getComputedStyle(target)
      if (style.display !== 'none' && style.visibility !== 'hidden') {
        setMobileMenuAnchor(target)
      }
    }
  }

  // Clean up anchor if it becomes invalid (removed from DOM or hidden)
  useEffect(() => {
    if (mobileMenuAnchor && typeof document !== 'undefined') {
      const checkAnchor = () => {
        if (!document.body.contains(mobileMenuAnchor)) {
          setMobileMenuAnchor(null)
          return
        }
        const style = window.getComputedStyle(mobileMenuAnchor)
        if (style.display === 'none' || style.visibility === 'hidden') {
          setMobileMenuAnchor(null)
        }
      }
      
      // Check periodically while menu is open
      const interval = setInterval(checkAnchor, 100)
      
      return () => {
        clearInterval(interval)
      }
    }
  }, [mobileMenuAnchor])

  const handleMobileMenuClose = (event, reason) => {
    // Prevent closing if menu was just opened (within 150ms) to avoid immediate close
    if (menuOpenTimeRef.current && Date.now() - menuOpenTimeRef.current < 150) {
      return
    }
    // Only close on backdrop click or escape key, allow programmatic close
    if (reason === 'backdropClick' || reason === 'escapeKeyDown' || !reason) {
      setMobileMenuAnchor(null)
      menuOpenTimeRef.current = null
    }
  }

  function closeDialog() {
    setSelectedPatient(null)
  }

  async function refreshBookedSlots() {
    setRefreshing(true)
    await refreshData()
    setRefreshing(false)
  }

  const isMobile = screenSize.width < 768


  // Styled container for the modernized header
  const ModernHeaderContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 20px',
    gap: '16px',
    zIndex: 11,
    position: 'relative',
    margin: 0,
    marginTop: 0,
    marginBottom: 0,
    overflow: 'visible',
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    borderBottom: '1px solid rgba(16, 185, 129, 0.12)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
    [theme.breakpoints.down('md')]: {
      padding: '6px 16px',
      gap: '12px',
    },
  }))

  const ClinicInfoBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 16px',
    backgroundColor: 'rgba(16, 185, 129, 0.06)',
    borderRadius: '10px',
    border: '1px solid rgba(16, 185, 129, 0.15)',
    [theme.breakpoints.down('sm')]: {
      padding: '6px 12px',
      gap: '8px',
    },
  }))

  return (
    <ModernHeaderContainer>
      {/* Desktop: Full Layout */}
      {!isMobile && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0, overflow: 'visible' }}>
            {/* Mentor Name, Check-in Time, and Status - Structured Together */}
            {mentorProfileData && (
              <ClinicInfoBox>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <ClinicAssistantNameButton
                      name={mentorProfileData?.name || mentorProfileData?.firstName}
                      onClick={isClinicAssistantFlow && onAssistantNameClick ? onAssistantNameClick : undefined}
                      variant="subtitle2"
                    />
                    {isPeekingIn && (
                      <Chip
                        icon={<VisibilityOffIcon sx={{ fontSize: 14 }} />}
                        label="Peeking Privately"
                        size="small"
                        onClick={handleCheckInPrompt}
                        sx={{
                          height: '20px',
                          fontSize: '10px',
                          fontWeight: 600,
                          backgroundColor: 'rgba(116, 80, 170, 0.15)',
                          color: '#7450aa',
                          border: '1px solid rgba(116, 80, 170, 0.3)',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(116, 80, 170, 0.25)',
                            borderColor: 'rgba(116, 80, 170, 0.5)',
                          },
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    {mentorProfileData?.specialization && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(100, 116, 139, 0.75)',
                          fontSize: '11px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={mentorProfileData?.specialization}
                      >
                        {mentorProfileData?.specialization}
                      </Typography>
                    )}
                    {checkedInTime && (
                      <>
                        {mentorProfileData?.specialization && (
                          <Typography variant="caption" sx={{ color: 'rgba(100, 116, 139, 0.5)', fontSize: '11px' }}>
                            •
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 12, color: 'rgba(16, 185, 129, 0.8)' }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(16, 185, 129, 0.9)',
                              fontSize: '11px',
                              fontWeight: 600,
                            }}
                          >
                            Checked in: {checkedInTime.format('h:mm A')}
                          </Typography>
                        </Box>
                      </>
                    )}
                    {!isCheckedIn && !isPeekingIn && (
                      <>
                        {mentorProfileData?.specialization && (
                          <Typography variant="caption" sx={{ color: 'rgba(100, 116, 139, 0.5)', fontSize: '11px' }}>
                            •
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LoginIcon sx={{ fontSize: 14 }} />}
                          onClick={handleCheckInPrompt}
                          sx={{
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '2px 8px',
                            height: '22px',
                            borderColor: 'rgba(16, 185, 129, 0.4)',
                            color: 'rgba(16, 185, 129, 0.9)',
                            '&:hover': {
                              borderColor: 'rgba(16, 185, 129, 0.6)',
                              backgroundColor: 'rgba(16, 185, 129, 0.08)',
                            },
                          }}
                        >
                          Check In
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              </ClinicInfoBox>
            )}

            {/* Tabs */}
            {!hideTabs && (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1,
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  [theme.breakpoints.down('md')]: {
                    gap: 0.5,
                  },
                }}
              >
                <BaseUITabsComponent
                  onChange={handleClick}
                  value={selectedTab}
                  containerStyle={{ marginLeft: 0, width: '100%' }}
                  containerClassName="fs-6 gap-1"
                  tabs={MENTOR_CLINIC_BOOKING_TABS_LIST?.filter((tab) => {
                    if (excludeTabs?.includes(tab?.id)) {
                      return false
                    }
                    if (getAllRolesInBoolean().isDoctor) {
                      return tab?.roleType?.includes('ROLE_DOCTOR')
                    } else if (getAllRolesInBoolean().isClinicAssistant) {
                      return tab?.roleType?.includes('ROLE_CLINIC_ASSISTANT')
                    }
                    return tab?.roleType?.includes('ROLE_THERAPIST')
                  })}
                  getTabId={(tab) => tab?.id}
                  getTabLabel={(tab) => tab?.title}
                  getTabIcon={(tab) => tab?.icon}
                  alignContentInColumnForm={false}
                  hideTextInMobileMode={isMobile}
                  incicatorColor="emrSecondary"
                  variant="contained"
                  unselectedColor="rgba(71, 85, 105, 0.85)"
                  selectedColor="rgba(16, 185, 129, 0.95)"
                  hoverItemColor="rgba(16, 185, 129, 0.9)"
                />
              </Box>
            )}
          </Box>

          {/* Practitioner Button removed from main header - only shown in floating header */}
          {children}

          {/* Right Side Actions - Desktop */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            {/* Refresh Button */}
            {!hideRefreshButton && (
              <Tooltip title="Refresh Calendar" arrow>
                <span>
                  <IconButton
                    onClick={refreshBookedSlots}
                    disabled={refreshing}
                    sx={{
                      color: 'rgba(16, 185, 129, 0.9)',
                      backgroundColor: 'rgba(16, 185, 129, 0.08)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      padding: '8px',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.12)',
                        borderColor: 'rgba(16, 185, 129, 0.3)',
                        transform: 'scale(1.05)',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        borderColor: 'rgba(0, 0, 0, 0.08)',
                        color: 'rgba(0, 0, 0, 0.26)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {refreshing ? (
                      <CircularProgress size={18} sx={{ color: 'rgba(16, 185, 129, 0.7)' }} />
                    ) : (
                      <RefreshIcon sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Checkout Button - Stands Out with Red/Exit Color */}
            {customExitButton ? (
              customExitButton
            ) : (
              <>
                {!hideCheckinDetails && (
                  <Box sx={{ flexShrink: 0 }}>
                    <CheckoutButtonComponent
                      text="Checkout"
                      buttonColor="error"
                      styleProp={{
                        flexDirection: 'row',
                        fontSize: '14px',
                        fontWeight: 700,
                        padding: '8px 20px',
                        height: 'auto',
                        minWidth: '110px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#dc2626',
                          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                        },
                        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                      }}
                      showExitButtonIfNotCheckedIn={true}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </>
      )}

      {/* Mobile: Simplified Layout - Mentor Info + Checkout + Search */}
      {isMobile && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
          {/* Left: Mentor Name, Check-in Time, and Status */}
          {mentorProfileData && (
            <Box 
              sx={{ 
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                padding: '6px 10px',
                backgroundColor: isPeekingIn 
                  ? 'rgba(116, 80, 170, 0.08)' 
                  : 'rgba(16, 185, 129, 0.06)',
                borderRadius: '8px',
                border: isPeekingIn
                  ? '1px solid rgba(116, 80, 170, 0.2)'
                  : '1px solid rgba(16, 185, 129, 0.15)',
                minWidth: 0,
                flex: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <ClinicAssistantNameButton
                  name={mentorProfileData?.name || mentorProfileData?.firstName}
                  onClick={isClinicAssistantFlow && onAssistantNameClick ? onAssistantNameClick : undefined}
                  variant="caption"
                  sx={{
                    fontSize: '12px',
                  }}
                />
                {isPeekingIn && (
                  <Chip
                    icon={<VisibilityOffIcon sx={{ fontSize: 10 }} />}
                    label="Peeking"
                    size="small"
                    onClick={handleCheckInPrompt}
                    sx={{
                      height: '18px',
                      fontSize: '9px',
                      fontWeight: 600,
                      backgroundColor: 'rgba(116, 80, 170, 0.15)',
                      color: '#7450aa',
                      border: '1px solid rgba(116, 80, 170, 0.3)',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(116, 80, 170, 0.25)',
                        borderColor: 'rgba(116, 80, 170, 0.5)',
                      },
                    }}
                  />
                )}
              </Box>
              {checkedInTime && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 10, color: 'rgba(16, 185, 129, 0.8)' }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(16, 185, 129, 0.9)',
                      fontSize: '10px',
                      fontWeight: 600,
                    }}
                  >
                    {checkedInTime.format('h:mm A')}
                  </Typography>
                </Box>
              )}
              {!isCheckedIn && !isPeekingIn && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LoginIcon sx={{ fontSize: 12 }} />}
                  onClick={handleCheckInPrompt}
                  sx={{
                    fontSize: '10px',
                    fontWeight: 600,
                    padding: '2px 6px',
                    height: '20px',
                    borderColor: 'rgba(16, 185, 129, 0.4)',
                    color: 'rgba(16, 185, 129, 0.9)',
                    mt: 0.25,
                    '&:hover': {
                      borderColor: 'rgba(16, 185, 129, 0.6)',
                      backgroundColor: 'rgba(16, 185, 129, 0.08)',
                    },
                  }}
                >
                  Check In
                </Button>
              )}
            </Box>
          )}

          {/* Center: Checkout Button - Stands Out */}
          {customExitButton ? (
            customExitButton
          ) : (
            <>
              {!hideCheckinDetails && (
                <Box sx={{ flexShrink: 0 }}>
                  <CheckoutButtonComponent
                    text="Checkout"
                    buttonColor="error"
                    styleProp={{
                      flexDirection: 'row',
                      fontSize: '12px',
                      fontWeight: 700,
                      padding: '6px 14px',
                      height: 'auto',
                      minWidth: '90px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#dc2626',
                        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                      },
                      boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
                    }}
                    showExitButtonIfNotCheckedIn={true}
                  />
                </Box>
              )}
            </>
          )}

          {/* Center: Search Patient Icon */}
          {onOpenPatientSearch && (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <IconButton
                onClick={onOpenPatientSearch}
                sx={{
                  color: '#6779e3',
                  backgroundColor: 'rgba(103, 121, 227, 0.1)',
                  border: '1.5px solid rgba(103, 121, 227, 0.3)',
                  padding: '10px',
                  '&:hover': {
                    backgroundColor: 'rgba(103, 121, 227, 0.15)',
                    borderColor: 'rgba(103, 121, 227, 0.5)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <SearchIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Box>
          )}

          {/* Right: 3-dots Menu */}
          <Box sx={{ flexShrink: 0 }}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                handleMobileMenuOpen(e)
              }}
              onMouseDown={(e) => {
                e.stopPropagation()
              }}
              sx={{
                color: 'rgba(16, 185, 129, 0.9)',
                backgroundColor: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                padding: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(16, 185, 129, 0.12)',
                  borderColor: 'rgba(16, 185, 129, 0.3)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <MoreVertIcon sx={{ fontSize: 22 }} />
            </IconButton>

            {/* Mobile Menu */}
            <Menu
              anchorEl={mobileMenuAnchor && typeof document !== 'undefined' && document.body.contains(mobileMenuAnchor) ? mobileMenuAnchor : null}
              open={Boolean(mobileMenuAnchor && typeof document !== 'undefined' && mobileMenuAnchor && document.body.contains(mobileMenuAnchor))}
              onClose={(event, reason) => {
                // Only close on backdrop click or escape key, not on menu item clicks or other events
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                  handleMobileMenuClose(event, reason)
                }
                // Ignore all other close reasons to prevent immediate closing
              }}
              closeAfterTransition={false}
              MenuListProps={{
                'aria-labelledby': 'mobile-menu-button',
                onClick: (e) => {
                  // Prevent clicks inside menu from bubbling up and closing the menu
                  e.stopPropagation()
                },
                onMouseDown: (e) => {
                  // Prevent mouse down from closing the menu
                  e.stopPropagation()
                },
                onWheel: (e) => {
                  // Prevent scroll events from closing the menu
                  e.stopPropagation()
                },
                onScroll: (e) => {
                  // Prevent scroll events from closing the menu
                  e.stopPropagation()
                },
                onTouchMove: (e) => {
                  // Prevent touch move events from closing the menu
                  e.stopPropagation()
                },
              }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                  borderRadius: '12px',
                  border: '1px solid rgba(16, 185, 129, 0.1)',
                  zIndex: 9999,
                  position: 'relative',
                  pointerEvents: 'auto',
                  '& .MuiMenuItem-root': {
                    pointerEvents: 'auto',
                    userSelect: 'none',
                  },
                  '& .MuiMenuList-root': {
                    pointerEvents: 'auto',
                  },
                },
                onClick: (e) => {
                  // Prevent clicks on paper from closing menu
                  e.stopPropagation()
                },
                onMouseDown: (e) => {
                  // Prevent mouse down from closing menu
                  e.stopPropagation()
                },
              }}
              BackdropProps={{
                sx: {
                  zIndex: 9998,
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  pointerEvents: 'auto',
                },
                onClick: (e) => {
                  // Close on backdrop click only
                  e.stopPropagation()
                  setMobileMenuAnchor(null)
                },
                onMouseDown: (e) => {
                  // Prevent any mouse down events from propagating
                  e.stopPropagation()
                },
                onTouchStart: (e) => {
                  // Prevent touch events from propagating
                  e.stopPropagation()
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              disableAutoFocusItem
              disableRestoreFocus
              disableScrollLock
            >
              {/* Clinic Information */}
              {!hideClinicDetails && currentClinic && (
                <>
                  <MenuItem
                    disabled
                    sx={{
                      opacity: 1,
                      py: 1.5,
                      '&.Mui-disabled': {
                        opacity: 1,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      <LocationOnIcon sx={{ fontSize: 18, color: 'rgba(16, 185, 129, 0.8)' }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: 'rgba(15, 23, 42, 0.95)',
                            fontSize: '14px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {currentClinic?.title}
                        </Typography>
                        {currentClinic?.clinicBrandName && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(100, 116, 139, 0.75)',
                              fontSize: '11px',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {currentClinic?.clinicBrandName}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                  <Divider />
                </>
              )}

              {/* Tabs in Menu */}
              {!hideTabs && (
                <>
                  {MENTOR_CLINIC_BOOKING_TABS_LIST?.filter((tab) => {
                    if (excludeTabs?.includes(tab?.id)) {
                      return false
                    }
                    if (getAllRolesInBoolean().isDoctor) {
                      return tab?.roleType?.includes('ROLE_DOCTOR')
                    } else if (getAllRolesInBoolean().isClinicAssistant) {
                      return tab?.roleType?.includes('ROLE_CLINIC_ASSISTANT')
                    }
                    return tab?.roleType?.includes('ROLE_THERAPIST')
                  }).map((tab) => {
                    const TabIcon = tab?.icon
                    return (
                      <MenuItem
                        key={tab?.id}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleClick(tab?.id)
                          handleMobileMenuClose()
                        }}
                        selected={selectedTab === tab?.id}
                        sx={{
                          py: 1.5,
                          pointerEvents: 'auto',
                          cursor: 'pointer',
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(16, 185, 129, 0.15)',
                            },
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                          {TabIcon && <TabIcon sx={{ fontSize: 18 }} />}
                          <Typography sx={{ fontSize: '14px', fontWeight: selectedTab === tab?.id ? 600 : 400 }}>
                            {tab?.title}
                          </Typography>
                        </Box>
                      </MenuItem>
                    )
                  })}
                  <Divider />
                </>
              )}

              {/* Refresh */}
              {!hideRefreshButton && (
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    refreshBookedSlots()
                    handleMobileMenuClose()
                  }}
                  disabled={refreshing}
                  sx={{
                    py: 1.5,
                    pointerEvents: refreshing ? 'none' : 'auto',
                    cursor: refreshing ? 'not-allowed' : 'pointer',
                    '&.Mui-disabled': {
                      opacity: 0.6,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    {refreshing ? (
                      <CircularProgress size={18} sx={{ color: 'rgba(16, 185, 129, 0.7)' }} />
                    ) : (
                      <RefreshIcon sx={{ fontSize: 18 }} />
                    )}
                    <Typography sx={{ fontSize: '14px' }}>Refresh Calendar</Typography>
                  </Box>
                </MenuItem>
              )}

              {/* Patient Search */}
              {onOpenPatientSearch && (
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenPatientSearch()
                    handleMobileMenuClose()
                  }}
                  sx={{ 
                    py: 1.5,
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <SearchIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontSize: '14px' }}>Search / Add Patient</Typography>
                  </Box>
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Box>
      )}
      <Dialog
        open={!!selectedPatient}
        onClose={closeDialog}
        PaperComponent={PastTherapyReportDialogPaper}
      >
        <NewBookingPopup
          selectedPatient={selectedPatient}
          refreshData={refreshBookedSlots}
          mentorsList={mentorsList}
          appointmentTypesList={appointmentTypesList}
          closeDialog={closeDialog}
          patientHistoryGetApiSignature={patientHistoryGetApiSignature}
          hideSendToDoctorButton={hideSendToDoctorButton}
          filterOutTabs={
            hidePatientHistoryRelatedTabs
              ? [
                  MENTOR_NEW_BOOKING_TABS?.patientHistory?.id,
                  MENTOR_NEW_BOOKING_TABS?.patientReports?.id,
                ]
              : []
          }
        />
      </Dialog>

      {/* Check-in Dialog */}
      {checkInDialogProps && (
        <CheckInDialogComponent
          key={`checkin-dialog-${dialogRoomData?.id || 'default'}`}
          {...checkInDialogProps}
        />
      )}

      <CheckinRequestPromptDialog
        open={requestCheckoutDialogOpen}
        roomData={requestCheckoutRoomData}
        clinicLocationData={currentClinic}
        onClose={() => {
          if (!requestCheckoutLoading) {
            setRequestCheckoutDialogOpen(false)
            setRequestCheckoutRoomData(null)
          }
        }}
        onSubmit={handleSubmitCheckoutRequest}
        loading={requestCheckoutLoading}
      />
    </ModernHeaderContainer>
  )
}

// Memoize HeaderItems to prevent unnecessary re-renders
// Use a custom comparison function that ignores children prop
// since children (PractitionerButton) is controlled separately
export default memo(HeaderItems, (prevProps, nextProps) => {
  // Compare all props except children
  return (
    prevProps.clinicAssociationData === nextProps.clinicAssociationData &&
    prevProps.clinicId === nextProps.clinicId &&
    prevProps.clinicBrandId === nextProps.clinicBrandId &&
    prevProps.selectedTab === nextProps.selectedTab &&
    prevProps.setSelectedTab === nextProps.setSelectedTab &&
    prevProps.mentorProfileData === nextProps.mentorProfileData &&
    prevProps.refreshData === nextProps.refreshData &&
    prevProps.hideRefreshButton === nextProps.hideRefreshButton &&
    prevProps.hideClinicDetails === nextProps.hideClinicDetails &&
    prevProps.hideCheckinDetails === nextProps.hideCheckinDetails &&
    prevProps.hideTabs === nextProps.hideTabs &&
    prevProps.mentorsList === nextProps.mentorsList &&
    prevProps.searchPatientApiSignature === nextProps.searchPatientApiSignature &&
    prevProps.appointmentTypesList === nextProps.appointmentTypesList &&
    prevProps.createPatientApiSignature === nextProps.createPatientApiSignature &&
    prevProps.excludeTabs === nextProps.excludeTabs &&
    prevProps.patientHistoryGetApiSignature === nextProps.patientHistoryGetApiSignature &&
    prevProps.hidePatientHistoryRelatedTabs === nextProps.hidePatientHistoryRelatedTabs &&
    prevProps.hidePatientAddButton === nextProps.hidePatientAddButton &&
    prevProps.hideCheckInButton === nextProps.hideCheckInButton &&
    prevProps.customExitButton === nextProps.customExitButton &&
    prevProps.hideSendToDoctorButton === nextProps.hideSendToDoctorButton &&
    prevProps.onOpenPatientSearch === nextProps.onOpenPatientSearch &&
    // Compare practitionerButtonProps primitive values
    prevProps.practitionerButtonProps?.imageUrl === nextProps.practitionerButtonProps?.imageUrl &&
    prevProps.practitionerButtonProps?.managerId === nextProps.practitionerButtonProps?.managerId &&
    prevProps.practitionerButtonProps?.buttonText === nextProps.practitionerButtonProps?.buttonText &&
    prevProps.practitionerButtonProps?.isActive === nextProps.practitionerButtonProps?.isActive &&
    prevProps.onOpenPractitionerDrawer === nextProps.onOpenPractitionerDrawer
    // Note: We intentionally don't compare children prop for backward compatibility
    // If practitionerButtonProps is provided, it takes precedence over children
  )
})
